import type { BrowserDisplaySession } from "@cmmd-forge/contracts";
import {
  formatBrowserErrorDescription,
  normalizeBrowserError,
} from "@cmmd-forge/shared/browserErrors";
import { shouldAttachRemotePixelDisplay } from "@cmmd-forge/shared/browserPresentation";

export function normalizeBrowserDisplayForPreview(
  display: BrowserDisplaySession,
): BrowserDisplaySession {
  if (
    display.transport === "unknown" &&
    display.status === "unsupported" &&
    display.reason === "transport_unavailable"
  ) {
    return {
      transport: "remote-webrtc",
      status: "degraded",
      reason: "transport_unavailable",
      detail: "Remote WebRTC browser display is not attached yet.",
      ...(display.viewport ? { viewport: display.viewport } : {}),
      ...(display.devicePixelRatio ? { devicePixelRatio: display.devicePixelRatio } : {}),
      reconnect: display.reconnect ?? { status: "ready", attempt: 0 },
    };
  }
  if (
    display.transport === "remote-webrtc" &&
    display.status === "failed" &&
    display.reason !== "network_unreachable" &&
    display.reason !== "unsupported_host" &&
    /\b(?:failed before a video stream was ready|attach timed out before a video stream was ready)\b/i.test(
      display.detail ?? "",
    )
  ) {
    return {
      transport: "remote-webrtc",
      status: "degraded",
      reason: "transport_unavailable",
      detail: "Remote WebRTC browser display needs to reconnect for this tab.",
      ...(display.viewport ? { viewport: display.viewport } : {}),
      ...(display.devicePixelRatio ? { devicePixelRatio: display.devicePixelRatio } : {}),
      ...(display.iceServers ? { iceServers: display.iceServers } : {}),
      reconnect: { status: "ready", attempt: (display.reconnect?.attempt ?? 0) + 1 },
    };
  }
  return display;
}

export function formatRemoteWebRtcDisplayError(error: unknown): string {
  const normalized = normalizeBrowserError(error);
  const technicalDetail = normalized.technicalDetail?.trim();
  if (
    technicalDetail &&
    /Remote WebRTC browser display|WebRTC browser display|RTCPeerConnection|RTCSessionDescription|setRemoteDescription|setLocalDescription|ICE candidate|ffmpeg|Xvfb|x11grab/i.test(
      technicalDetail,
    )
  ) {
    return technicalDetail;
  }

  const message = formatBrowserErrorDescription(error);
  if (/browser runtime in time|timed out|timeout/i.test(message)) {
    return "Remote WebRTC browser display attach timed out before a video stream was ready.";
  }
  if (/RTCPeerConnection/i.test(message)) {
    return "This browser host does not support RTCPeerConnection, so live WebRTC display cannot attach.";
  }
  if (message.startsWith("Forge could not complete the browser action.")) {
    return "Remote WebRTC browser display failed before a video stream was ready.";
  }
  return message;
}

/**
 * Whether a remote WebRTC display should be attached.
 *
 * The implementation lives in `@cmmd-forge/shared/browserPresentation` alongside
 * the surface decision it feeds; this alias keeps the existing call sites
 * pointing at one copy of the predicate rather than a second.
 */
export const shouldAttachRemoteWebRtcDisplay = shouldAttachRemotePixelDisplay;

export function remoteWebRtcBrowserSurfaceInstanceKey(input: {
  readonly browserId: string | null | undefined;
  readonly sessionId: string | null | undefined;
  readonly viewport: { readonly width: number; readonly height: number } | null | undefined;
  readonly display: BrowserDisplaySession | undefined;
}): string {
  return [
    input.browserId ?? "no-browser",
    input.sessionId ?? "no-session",
    input.viewport ? `${input.viewport.width}x${input.viewport.height}` : "no-viewport",
    input.display?.transport ?? "no-transport",
  ].join(":");
}

export function staleRemoteWebRtcConnectingDisplayFailure(
  display: BrowserDisplaySession | undefined,
): BrowserDisplaySession | null {
  if (display?.transport !== "remote-webrtc" || display.status !== "connecting") {
    return null;
  }
  return {
    transport: "remote-webrtc",
    status: "failed",
    reason: "unknown",
    detail:
      "Remote WebRTC browser display stayed in connecting state before a video stream was ready.",
    ...(display.viewport ? { viewport: display.viewport } : {}),
    ...(display.devicePixelRatio ? { devicePixelRatio: display.devicePixelRatio } : {}),
    ...(display.iceServers ? { iceServers: display.iceServers } : {}),
    reconnect: { status: "exhausted", attempt: display.reconnect?.attempt ?? 0 },
  };
}
